import { createSlug } from "./helpers";

export const SITE_URL = "https://muzisyen.app";
export const SITE_NAME = "muzisyen.app";
export const DEFAULT_TITLE =
  "muzisyen.app - Müzisyen İlanları, Stüdyolar ve Enstrüman Pazarı";
export const DEFAULT_DESCRIPTION =
  "Türkiye'nin müzisyen platformu: grup ve eleman ilanları, prova ve kayıt stüdyoları, ikinci el enstrüman pazarı, müzik haberleri, konserler ve soru-cevap.";
export const DEFAULT_IMAGE = SITE_URL + "/images/og-default.jpg";
export const LOGO_URL = SITE_URL + "/images/logo.png";
export const SOCIAL_PROFILES = [
  "https://facebook.com/muzisyenapp",
  "https://instagram.com/muzisyen.app",
  "https://twitter.com/muzisyenapp",
  "https://youtube.com/@muzisyenapp",
];

export const stripHtml = (html) => {
  if (!html) return "";
  const withoutTags = String(html)
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ");
  const textarea = document.createElement("textarea");
  textarea.innerHTML = withoutTags;
  return textarea.value.replace(/\s+/g, " ").trim();
};

export const truncate = (text, max = 160) => {
  if (!text || text.length <= max) return text || "";
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  const trimmed = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return trimmed.replace(/[\s,.;:–-]+$/, "") + "…";
};

// İlk dolu adaydan HTML'i temizlenmiş, 160 karaktere kısaltılmış açıklama üretir.
export const toDescription = (...candidates) => {
  for (const candidate of candidates) {
    const text = stripHtml(candidate);
    if (text) return truncate(text);
  }
  return "";
};

export const absoluteUrl = (url) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return "https:" + url;
  return SITE_URL + (url.startsWith("/") ? "" : "/") + url;
};

const withSlug = (base, id, text) => {
  const slug = createSlug(text || "");
  return slug ? `${base}/${id}/${slug}` : `${base}/${id}`;
};

// Detay sayfalarının tek doğru (canonical) adresleri.
export const contentPath = {
  studio: (item) => withSlug("/app/studyo-detay", item.id, item.name),
  announcement: (item) => withSlug("/app/ilan-detay", item.id, item.title),
  news: (item) => withSlug("/app/haber-detay", item.id, item.title),
  event: (item) => withSlug("/app/etkinlik-detay", item.id, item.title),
  product: (item) => withSlug("/app/urun-detay", item.id, item.name),
  question: (item) => withSlug("/app/soru-cevap", item.id, item.subject),
  track: (item) => withSlug("/app/sarki-detay", item.id, item.name),
};

// URL'deki slug başlıkla uyuşmuyorsa (eksik, eski başlık vb.) doğru adrese yönlendirilmeli.
export const needsSlugRedirect = (currentSlug, text) =>
  (currentSlug || "") !== (createSlug(text || "") || "");

export const isNotFoundError = (error) => error?.response?.status === 404;

const personLd = (user) =>
  user?.username
    ? {
        "@type": "Person",
        name: user.username,
        url: `${SITE_URL}/${user.username}`,
      }
    : undefined;

const publisherLd = {
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: LOGO_URL },
};

const compact = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
  );

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Ana Sayfa", path: "/" }, ...items].map(
    (item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })
  ),
});

export const newsArticleLd = (news) =>
  compact({
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: truncate(news.title, 110),
    description: toDescription(news.short_description, news.description),
    image: [absoluteUrl(news.image?.image)].filter(Boolean),
    datePublished: news.create_time,
    dateModified: news.last_edited_at || news.update_time || news.create_time,
    author: personLd(news.user) || publisherLd,
    publisher: publisherLd,
    mainEntityOfPage: absoluteUrl(contentPath.news(news)),
  });

export const eventLd = (event) =>
  event.event_date && event.event_place
    ? compact({
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.title,
        description: toDescription(event.short_description, event.description),
        image: [absoluteUrl(event.image?.image)].filter(Boolean),
        startDate: event.event_date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: event.event_place,
          address: event.event_place,
        },
        performer: event.artist_name
          ? { "@type": "PerformingGroup", name: event.artist_name }
          : undefined,
        url: absoluteUrl(contentPath.event(event)),
      })
    : null;

export const productLd = (product) =>
  compact({
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: toDescription(product.short_description, product.description),
    image: [
      absoluteUrl(product.image?.image),
      ...(product.gallery || []).map((item) => absoluteUrl(item?.image)),
    ].filter(Boolean),
    sku: String(product.id),
    category: product.category?.full_category_name || product.category?.name,
    offers:
      Number(product.price) > 0
        ? {
            "@type": "Offer",
            price: Number(product.price).toFixed(2),
            priceCurrency: "TRY",
            availability: "https://schema.org/InStock",
            itemCondition:
              product.status === "Yeni"
                ? "https://schema.org/NewCondition"
                : "https://schema.org/UsedCondition",
            url: absoluteUrl(contentPath.product(product)),
            seller: personLd(product.user),
          }
        : undefined,
  });

// Yalnızca alan adı içeren gerçek URL'ler ("http://" gibi boş değerler hariç).
const isUrl = (value) => /^https?:\/\/[^\s/]+\.[^\s/]{2,}/i.test(value || "");

const singleLine = (text) => (text ? String(text).replace(/\s+/g, " ").trim() : undefined);

export const studioLd = (studio) =>
  compact({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": absoluteUrl(contentPath.studio(studio)),
    name: studio.name,
    description: toDescription(studio.info_desc),
    image: [absoluteUrl(studio.image?.image)].filter(Boolean),
    telephone: singleLine(studio.phone),
    url: absoluteUrl(contentPath.studio(studio)),
    address: compact({
      "@type": "PostalAddress",
      streetAddress: singleLine(studio.address),
      addressLocality: studio.town?.name,
      addressRegion: studio.city?.name,
      addressCountry: "TR",
    }),
    sameAs: [studio.site_name, studio.facebook, studio.instagram, studio.youtube]
      .filter(isUrl),
  });

export const qaPageLd = (question, answers) =>
  answers?.length
    ? {
        "@context": "https://schema.org",
        "@type": "QAPage",
        mainEntity: compact({
          "@type": "Question",
          name: question.subject,
          text: question.question_text || question.subject,
          answerCount: answers.length,
          datePublished: question.create_time,
          author: personLd(question.user),
          suggestedAnswer: answers.map((answer) =>
            compact({
              "@type": "Answer",
              text: answer.answer_text,
              datePublished: answer.create_time,
              author: personLd(answer.user),
              url: `${absoluteUrl(contentPath.question(question))}#cevap-${answer.id}`,
            })
          ),
        }),
      }
    : null;

export const profilePageLd = (musician, fullName, avatarUrl) =>
  compact({
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    url: `${SITE_URL}/${musician.username}`,
    mainEntity: compact({
      "@type": "Person",
      name: fullName,
      alternateName: "@" + musician.username,
      description: toDescription(musician.biography),
      image: absoluteUrl(avatarUrl),
      url: `${SITE_URL}/${musician.username}`,
      jobTitle: "Müzisyen",
      knowsAbout: [
        ...(musician.instruments || []).map((item) => item?.name),
        ...(musician.genres_list || []).map((item) => item?.name),
      ].filter(Boolean),
      homeLocation: musician.city_name
        ? {
            "@type": "Place",
            name: [musician.town_name, musician.city_name].filter(Boolean).join(", "),
          }
        : undefined,
      sameAs: [
        musician.website_url,
        musician.instagram_url,
        musician.youtube_url,
        musician.spotify_url,
        musician.soundcloud_url,
        musician.applemusic_url,
        musician.facebook_url,
        musician.twitter_url,
      ].filter(isUrl),
    }),
  });
